import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import {
  FluentProvider,
  webLightTheme,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
} from '@fluentui/react-components';
import { notificationApi, WrappedFloatingBannerContainer } from '@bingads-webui-react/notification-center';
import { HelpLink } from '@bingads-webui-react/help-link';
import config from '@bingads-webui-component-legacy/config/index';

import { inlineUpdateTasks } from './data-source';

export function PauseScheduledImportDialog({
  type = 'pause',
  i18n,
  taskIds,
  showInlineEdit = false,
  onEdit,
  onContinue,
  onDismiss,
  activity = { trace: () => {} },
}) {
  const [loading, setLoading] = useState(false);

  const editImport = async () => {
    setLoading(true);
    activity.trace(JSON.stringify({
      event: 'EditImport',
      taskIds,
    }));
    const scope = {
      aid: config.get('CurrentAccount').Id,
      cid: config.get('CurrentCustomer').Id,
    };
    try {
      const res = await inlineUpdateTasks(scope, taskIds.map(v => ({
        taskId: v,
        preference: {
          UpdateStatusForCampaigns: false,
        },
      })));
      if (res.statusCounter && res.statusCounter.success === taskIds.length) {
        notificationApi.showInfo(i18n.getString('Import_Preference_InlineUpdate_Success'));
      } else {
        notificationApi.showWarning(i18n.getString('Import_Preference_InlineUpdate_Error'));
      }
      onEdit();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog defaultOpen>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{type === 'pause' ? i18n.getString(_TL_('Pause scheduled import')) : i18n.getString(_TL_('Delete scheduled import'))}</DialogTitle>
            <DialogContent style={{ paddingBottom: 30 }}>
              {showInlineEdit ? i18n.getString(_TL_("Are you sure? To prevent your campaign’s status from being overwritten, please navigate to your import's options and deselect Campaigns under Customize Status or uncheck Status under Updates to existing items.")) : i18n.getString(_TL_('Are you sure?'))}
              { showInlineEdit &&
              <>
                &nbsp;
                <HelpLink query="help:app1">
                  {i18n.getString(_TL_('Learn more'))}
                </HelpLink>
              </>
            }
            </DialogContent>
            <DialogActions
              fluid
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: '8px',
              }}
            >
              <div>
                { showInlineEdit && <Button appearance="secondary" onClick={editImport} disabled={loading}>{i18n.getString(_TL_('Prevent status overwrite'))}</Button> }
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button appearance="primary" onClick={onContinue}>{type === 'pause' ? i18n.getString(_TL_('Continue with import pause')) : i18n.getString(_TL_('Continue with import deletion'))}</Button>
                <Button appearance="secondary" onClick={onDismiss}>{i18n.getString(_TL_('Cancel'))}</Button>
              </div>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <WrappedFloatingBannerContainer i18n={i18n} />
    </>
  );
}

PauseScheduledImportDialog.propTypes = {
  type: PropTypes.string,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  showInlineEdit: PropTypes.bool,
  taskIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onEdit: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  activity: PropTypes.shape({
    trace: PropTypes.func,
  }),
};

PauseScheduledImportDialog.defaultProps = {
  type: 'pause',
  showInlineEdit: false,
  activity: { trace: () => {} },
};

export const showPauseScheduledImportDialog = ({
  i18n, type, taskIds, onContinue, activity = { trace: () => {} },
}) => {
  if (!taskIds || taskIds.length === 0) {
    return false;
  }
  const aid = new URL(window.location.href).searchParams.get('aid');
  const sessionStoragekey = `ImportedCampaignStatusOverwrite-${aid}`;
  const storage = window.sessionStorage.getItem(sessionStoragekey);
  const showInlineEdit = storage && Date.now() - parseInt(storage, 10) < 60 * 60 * 1000;
  activity.trace(JSON.stringify({
    event: 'ShowPauseScheduledImportDialog',
    taskIds,
  }));
  const dialog = document.createElement('div');
  dialog.id = 'notification-dialog';
  document.body.appendChild(dialog);

  const dismiss = () => {
    ReactDOM.unmountComponentAtNode(dialog);
    document.body.removeChild(dialog);
  };

  ReactDOM.render(
    (
      <FluentProvider theme={webLightTheme}>
        <PauseScheduledImportDialog
          i18n={i18n}
          type={type}
          activity={activity}
          taskIds={taskIds}
          showInlineEdit={showInlineEdit}
          onEdit={() => {
            dismiss();
          }}
          onContinue={() => {
            activity.trace(JSON.stringify({
              event: 'Continue',
              taskIds,
            }));
            onContinue(taskIds);
            dismiss();
          }}
          onDismiss={() => {
            activity.trace(JSON.stringify({
              event: 'Dismiss',
              taskIds,
            }));
            dismiss();
          }}
        />
      </FluentProvider>
    ), dialog
  );
  return true;
};
